/* eslint-disable react/jsx-key */
import React from 'react';

import { FlexColumn, FlexRow } from '@editor/components/utilities/layouts';
import { LATEST_PRO_DESIGN_LIMIT } from '@editor/global/newPricingPageConfig';
import { SharedConfigStoreState } from '@editor/stores/sharedConfigStore/types';
import type { Workspace } from '@repo/api-client';
import { User } from '@repo/domain/user';
import BellIcon from '@repo/ui/components/Icon/BellIcon';
import ClockIcon from '@repo/ui/components/Icon/ClockIcon';
import StarIcon from '@repo/ui/components/Icon/StarIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { H6Plus, P2 } from '@repo/ui/components/Typography';
import { getIsNewThemeActive, themeStyle } from '@repo/ui/theming';
import { capitalizeFirstLetter } from '@repo/ui/utils';
import { buildImageProxyUrl } from '@repo/utils/url';

import {
  FREE_TRIAL_DAYS_AMOUNT,
  TRIAL_EXPIRE_NOTIFICATION_DAYS,
} from '../../global/constants';
import { useSharedConfigStore } from '../../stores/sharedConfigStore';
import MinimalTestimonial from '../Testimonial/MinimalTestimonial';
import Testimonial from '../Testimonial/Testimonial';
import { MinimalTestimonialSize } from '../Testimonial/types';
import TrustpilotReview from '../TrustpilotReview/TrustpilotReview';

import {
  BrandsImage,
  BrandsWrapper,
  DesignPreview,
  FreeTrialIconsContainer,
  FreeTrialIconWrapper,
  TestimonialWrapper,
} from './styles';
import {
  GetItemsFn,
  ImageComponentType,
  UpgradeModalConfigData,
  UpgradeModalContentType,
} from './types';

const unlimitedPremiumTemplates = (
  <P2>
    Unlimited <b>premium templates</b> and exclusive fonts
  </P2>
);

const advancedDesignFeatures = (
  <P2>
    Stand out with <b>advanced design features</b>
  </P2>
);

const unlimitedPremiumMockups = (
  <P2>
    <b>Unlimited premium mockups</b>, templates, illustrations, fonts and more
  </P2>
);

const moreProjectsAndExports = (
  <P2>
    <b>More projects & exports</b> (all vector and rastered file formats)
  </P2>
);

const generateImagesAndIllustrations = (
  <P2>
    <b>Generate images and illustrations</b> via text prompts
  </P2>
);

const aiPortraitAndLandscapeRatios = (
  <P2>
    Access to <b>portrait and landscape ratios</b>
  </P2>
);
const unlockAiTools = (
  <P2>
    Unlock all AI-powered tools to speed up your workflow with higher usage
    limits
  </P2>
);

const allAiTools = <P2>All AI-powered tools to speed up your workflow</P2>;

const KittlAICreditsInfo = (): React.ReactElement => {
  const { config } = useSharedConfigStore();

  const creditLimit = config?.creditLimits?.EXPERT;

  return (
    <P2>
      Kittl AI with up to <b>{creditLimit} credits per month</b>
    </P2>
  );
};

const kittlAiImageGenV2 = (
  <P2>
    Kittl AI&nbsp;<b>Image Generation v2</b>
    <br />
    highest quality images, new styles
  </P2>
);

const kittlAiSaveStyles = (
  <P2>
    <b>Unlock Image Style Reference</b>
  </P2>
);

const downloadHighQuality = (
  <P2>
    Download in <b>high-resolution and vector file format</b>
  </P2>
);

const accessTemplatesPhotos = (
  <P2>
    Access 10+ million{' '}
    <b>
      professional templates, illustrations, stock photos, fonts and textures
    </b>
  </P2>
);

const getAITools = (
  <P2>
    Get AI-powered tools to speed up your workflow with higher usage limits
  </P2>
);

const stayOnBrand = <P2>Stay on brand with brand kits and reusable assets</P2>;

const projectLimitReachedDescription = (
  workspace: Workspace | null
): string => {
  const projectCount = workspace?.designCount ?? 0;
  const projectLimit = workspace?.designsLimits ?? 0;

  if (projectCount > projectLimit) {
    return `You've created ${projectCount} projects - that's impressive. The limit of your current plan is ${projectLimit} projects. Upgrade your account and stand out with your designs.`;
  }

  return `You've completed ${projectCount}/${projectLimit} projects with us - that's impressive. Now, you're ready for the next step.
Upgrade your account and stand out with your designs.`;
};

const extraSpace = (amountOfSpace: string): React.ReactNode => (
  <P2>
    <b>{amountOfSpace} upload space</b> to fuel your creations
  </P2>
);

const HighResVectorDownloadContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      Unlimited <b>high-resolution downloads</b> (.png, .svg, .pdf)
    </P2>,
    unlimitedPremiumTemplates,
    advancedDesignFeatures,
  ];
};

const HighResVectorAccessContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      Unlimited <b>high-resolution downloads</b> (.png, .svg, .pdf)
    </P2>,
    <P2>
      Access to all <b>professional design templates</b>, assets & exclusive
      fonts
    </P2>,
    <P2>
      Create more stunning designs with <b>advanced design features</b>, incl.
      AI Vectoriser & Background Remover
    </P2>,
  ];
};
const getUserProDesignLimit = (
  user: User | null,
  sharedConfig: SharedConfigStoreState['config']
): number | string => {
  // The first limit is the most recent design limit
  const latestProDesignLimit =
    sharedConfig?.designLimits?.[0]?.PRO ?? LATEST_PRO_DESIGN_LIMIT;

  const userProDesignLimit = user?.designsLimitConfig.PRO;
  // designs limit is equal to zero if it is unlimited
  if (userProDesignLimit === 0) {
    return 'unlimited';
  }
  return userProDesignLimit ?? latestProDesignLimit;
};

// Free => Pro
const PremiumDesignUsage: GetItemsFn = (user, sharedConfig) => {
  let userProDesignLimit = getUserProDesignLimit(user, sharedConfig);
  userProDesignLimit =
    typeof userProDesignLimit === 'string'
      ? capitalizeFirstLetter(userProDesignLimit)
      : userProDesignLimit;

  return [
    <P2>
      <b>Unlimited</b> premium templates, illustrations, fonts and more
    </P2>,
    <P2>
      <b>{userProDesignLimit} projects & exports</b> (all vector and rastered
      file formats)
    </P2>,
    advancedDesignFeatures,
  ];
};
const PremiumBackgroundUsage = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Unlimited premium backgrounds </b>, illustrations, templates and more
    </P2>,
    <P2>
      <b>More projects & exports</b> (all vector and rastered file formats)
    </P2>,
    <P2>
      Stand out with <b>advanced design features</b>
    </P2>,
  ];
};
const PremiumTextureUsage = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Unlimited premium textures</b>, illustrations, templates and more
    </P2>,
    <P2>
      <b>More projects & exports</b> (all vector and rastered file formats)
    </P2>,
    advancedDesignFeatures,
  ];
};
const PremiumElementUsage = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Unlimited</b> premium illustrations, templates, fonts and more
    </P2>,
    moreProjectsAndExports,
    advancedDesignFeatures,
  ];
};
const PremiumCopilotElementsUsage = (): ReadonlyArray<React.ReactNode> => {
  return [downloadHighQuality, accessTemplatesPhotos, getAITools, stayOnBrand];
};

const PremiumLayoutUsage = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      Full library of predefined <b>drag-and-drop text layouts</b> and font
      pairings
    </P2>,
    unlimitedPremiumTemplates,
    advancedDesignFeatures,
  ];
};

const PremiumMockupUsage = (): ReadonlyArray<React.ReactNode> => {
  return [
    unlimitedPremiumMockups,
    <P2>
      <b>More projects & exports</b> (all vector and rastered file formats)
    </P2>,
    advancedDesignFeatures,
  ];
};

// Free => Pro
const ProjectLimitReachedContent: GetItemsFn = (user, sharedConfig) => {
  const userProDesignLimit = getUserProDesignLimit(user, sharedConfig);

  return [
    <P2>
      <b>Create {userProDesignLimit} projects</b> with pro accounts
    </P2>,
    unlimitedPremiumTemplates,
    advancedDesignFeatures,
  ];
};

// Pro => Expert
const ProProjectLimitReachedContent: GetItemsFn = (_user, sharedConfig) => {
  const expertCreditLimit = sharedConfig?.creditLimits.EXPERT ?? 80;

  return [
    <P2>
      <b>Create unlimited projects</b> with expert accounts
    </P2>,
    <P2>
      <b>Kittl AI with</b> up to {expertCreditLimit} monthly credits
    </P2>,
    extraSpace('10GB'),
  ];
};

const UploadLimitReachedContent = (
  amountOfSpace: string
): ReadonlyArray<React.ReactNode> => {
  return [
    extraSpace(amountOfSpace),
    unlimitedPremiumTemplates,
    advancedDesignFeatures,
  ];
};

const RemoveBackgroundContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Remove any backgrounds</b> with just one click
    </P2>,
    unlimitedPremiumTemplates,
    advancedDesignFeatures,
  ];
};

const UploadCustomFontsContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Upload custom fonts to your account</b> to use for any projects
    </P2>,
    unlimitedPremiumTemplates,
    advancedDesignFeatures,
  ];
};

const CustomArtboardSizeContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Custom artboard sizes</b> to create designs in any dimensions for any
      needs
    </P2>,
    unlimitedPremiumTemplates,
    advancedDesignFeatures,
  ];
};

const ImproveImageQualityContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Image enhancement</b> for best quality downloads
    </P2>,
    unlimitedPremiumTemplates,
    advancedDesignFeatures,
  ];
};

const removeKittlLogoContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Full commercial license</b>
    </P2>,
    <P2>
      <b>Kittl AI</b> credits
    </P2>,
    unlimitedPremiumMockups,
    moreProjectsAndExports,
  ];
};
const logoGenUpgradeContent: GetItemsFn = (_, sharedConfig) => {
  return [
    <P2>
      <b>Four generated logo options</b> to choose from one prompt
    </P2>,
    <P2>
      Kittl AI <b>Image Generation v2 - </b>highest quality images
    </P2>,
    <P2>
      Up to {sharedConfig?.creditLimits.EXPERT} AI credits to use per month
    </P2>,
  ];
};

const PremiumColorPalettesContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Global color palettes</b> for any project
    </P2>,
    unlimitedPremiumTemplates,
    advancedDesignFeatures,
  ];
};

const UpgradePlanWithLimitCredits = (): ReadonlyArray<React.ReactNode> => {
  return [
    generateImagesAndIllustrations,
    <P2>
      Access to <b>professional design templates</b> and exclusive fonts
    </P2>,
    <P2>
      Create more stunning designs with <b>advanced design features</b>
    </P2>,
  ];
};

const UpgradePlanWithMoreCreditsAsFreeUserAiPanelContent =
  (): ReadonlyArray<React.ReactNode> => {
    return [
      <KittlAICreditsInfo />,
      unlockAiTools,
      accessTemplatesPhotos,
      stayOnBrand,
    ];
  };
const UpgradePlanWithMoreCreditsAsProUserAiPanelContent =
  (): ReadonlyArray<React.ReactNode> => {
    return [
      <KittlAICreditsInfo />,
      unlimitedPremiumTemplates,
      advancedDesignFeatures,
    ];
  };

const ImageBackgroundRemovalContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      Unlimited <b>AI-powered background removing</b>
    </P2>,
    unlimitedPremiumTemplates,
    advancedDesignFeatures,
  ];
};

const UnlimitedBackgroundRemovalContent: GetItemsFn = (_, sharedConfig) => {
  return [
    <P2>
      Kittl AI & up to{' '}
      <b>{sharedConfig?.creditLimits.EXPERT} credits per month</b>
    </P2>,
    allAiTools,
    accessTemplatesPhotos,
  ];
};

const ImageGenV2UpgradeContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Advanced</b> Image Generation model - highest quality, more styles
    </P2>,
    <P2>
      Unlock <b>Aspect Ratios</b> for Image Generation
    </P2>,
    <P2>
      Unlock <b>Image Style Reference</b> to generate with custom styles
    </P2>,
  ];
};

const SaveStyleUpgradeContent = (): ReadonlyArray<React.ReactNode> => {
  return [kittlAiImageGenV2, kittlAiSaveStyles, unlimitedPremiumTemplates];
};

const SaveStyleUpgradeToExpertContent: GetItemsFn = (_, sharedConfig) => {
  return [
    <P2>
      <b>30 custom styles</b> for Image Generation
    </P2>,
    <P2>
      <b>{sharedConfig?.creditLimits.EXPERT} AI credits,</b> every month
    </P2>,
    extraSpace('100GB'),
  ];
};

const VectorizingContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>Convert images into vector graphics (SVG)</P2>,
    <P2>Unlimited AI-powered background removing</P2>,
    unlimitedPremiumTemplates,
  ];
};

const PodPresetsContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Dedicated POD artboard presets</b> and the freedom to create your own
    </P2>,
    unlimitedPremiumTemplates,
    <P2>
      <b>More projects & exports</b> (all vector and rastered file formats)
    </P2>,
    <P2>
      <b>Kittl AI</b> credits
    </P2>,
  ];
};

const PodDiscountsContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      Get up to <b>30% discount on all Kittl Print products</b>
    </P2>,
    <P2>
      Access to all <b>professional design templates</b>, assets & exclusive
      fonts
    </P2>,
    <P2>
      Create more stunning designs with <b>advanced design features, incl.</b>{' '}
      AI Vectoriser & Background Remover
    </P2>,
  ];
};

const AiRateLimitContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    generateImagesAndIllustrations,
    unlimitedPremiumTemplates,
    advancedDesignFeatures,
  ];
};

const BackgroundReplacerContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      Easily renew your product visuals with up to <b>3000 credits</b> per month
    </P2>,
    <P2>
      <b>Professional design templates</b> and <b>exclusive fonts</b>
    </P2>,
    <P2>
      Create more stunning designs with <b>advanced design features</b>
    </P2>,
  ];
};

const StandAlonePagesToolsContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Access to all premium features</b> in the Kittl Editor
    </P2>,
    <P2>
      <b>Generate images, illustrations & backgrounds</b> via text prompts
    </P2>,
    <P2>
      <b>Unlimited use</b> of AI Vectorizer and Background Remover
    </P2>,
  ];
};

const AiImageUpscalerContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>AI-powered Upscaling </b> for images
    </P2>,
    aiPortraitAndLandscapeRatios,
    unlimitedPremiumTemplates,
  ];
};

const AiImageUpscalerCreditsProContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <KittlAICreditsInfo />,
    <P2>
      <b>100GB upload space</b> to fuel your creations
    </P2>,
    <P2>High resolution product mockups</P2>,
  ];
};

const isNewThemeActive = getIsNewThemeActive();

export const getFreeTrialConfig = (customIconTheme?: {
  color: string;
}): UpgradeModalConfigData => {
  const iconTheme = {
    color: isNewThemeActive
      ? themeStyle.backgroundAlt
      : themeStyle.varBrandMain,
    ...(!!customIconTheme && { color: customIconTheme.color }),
  };

  return {
    title: `Start Your ${FREE_TRIAL_DAYS_AMOUNT}-Day Free Trial`,
    tag: 'TRIAL',
    description: 'Try it out before you commit. Cancel anytime.',
    content: (
      <FlexRow gap="20px">
        <FreeTrialIconsContainer>
          <FreeTrialIconWrapper>
            <StarIcon height="16px" theme={iconTheme} />
          </FreeTrialIconWrapper>
          <FreeTrialIconWrapper>
            <BellIcon height="16px" theme={iconTheme} />
          </FreeTrialIconWrapper>
          <FreeTrialIconWrapper>
            <ClockIcon height="16px" theme={iconTheme} />
          </FreeTrialIconWrapper>
        </FreeTrialIconsContainer>
        <FlexColumn gap="28px">
          <FlexColumn gap="4px">
            <H6Plus
              color={
                isNewThemeActive
                  ? themeStyle.varInkMain
                  : themeStyle.varBrandMain
              }
            >
              DAY 1
            </H6Plus>
            <P2>Start using Kittl’s premium features and templates.</P2>
          </FlexColumn>
          <FlexColumn gap="4px">
            <H6Plus
              color={
                isNewThemeActive
                  ? themeStyle.varInkMain
                  : themeStyle.varBrandMain
              }
            >
              DAY {FREE_TRIAL_DAYS_AMOUNT - TRIAL_EXPIRE_NOTIFICATION_DAYS}
            </H6Plus>
            <P2>We'll send you an email reminder, just to be safe.</P2>
          </FlexColumn>
          <FlexColumn gap="4px">
            <H6Plus
              color={
                isNewThemeActive
                  ? themeStyle.varInkMain
                  : themeStyle.varBrandMain
              }
            >
              DAY {FREE_TRIAL_DAYS_AMOUNT}
            </H6Plus>
            <P2>Last day of trial. Your subscription starts the next day.</P2>
          </FlexColumn>
        </FlexColumn>
      </FlexRow>
    ),
    buttonLabel: 'Start Free Trial',
  };
};

const LibrariesFreeTrialContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Brand Kits</b>
    </P2>,
    <P2>
      Access to <b>professional design templates</b> and exclusive fonts
    </P2>,
    <P2>
      Create more stunning designs with <b>advanced design features</b>
    </P2>,
  ];
};
const UnlimitedLibrariesContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      Unlimited <b>Brand Kits</b>
    </P2>,
    <P2>
      Access to <b>professional design templates</b> and exclusive fonts
    </P2>,
    <P2>
      Create more stunning designs with <b>advanced design features</b>
    </P2>,
  ];
};

const designGenUpgradeContent: GetItemsFn = (_, sharedConfig) => {
  return [
    <P2>
      <b>Four generated design options</b> to choose from one prompt
    </P2>,
    kittlAiImageGenV2,
    <P2>
      Up to {sharedConfig?.creditLimits.EXPERT} AI credits to use per month
    </P2>,
  ];
};

const privateModeUpgradeContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      <b>Kittl AI Advanced Image Generation</b> for stunning graphics
    </P2>,
    <P2>
      <b>Unlock private mode</b> for Kittl AI
    </P2>,
    <P2>
      Unlimited <b>premium templates</b> and exclusive fonts
    </P2>,
  ];
};

const eraseFillContent = (): ReadonlyArray<React.ReactNode> => {
  return [
    <P2>
      Unlock <b>all Kittl AI features</b> to speed up your workflow
    </P2>,
    unlimitedPremiumTemplates,
    <P2>
      Up to <b>100GB</b> Upload space
    </P2>,
  ];
};

const outOfCreditsTitle = 'Out of Credits?';
const outOfCredits = {
  title: outOfCreditsTitle,
  description:
    'You have reached your credit limit. Upgrade now for more credits to use Kittl AI.',
};

const outOfCreditsExpert = {
  title: outOfCreditsTitle,
  description:
    "You have reached your credit limit. To keep using Kittl AI, you can buy additional credits. These extra credits will be added to your account, and won't expire.",
};

const upgradeNowForMoreCreditsAndAiFeaturesDescription = {
  description: 'Upgrade now for more credits and advanced AI features',
};

type configProps = UpgradeModalConfigData & {
  getDescription?: (workspace: Workspace | null) => string;
  getItems?: GetItemsFn;
};

const config: Record<UpgradeModalContentType, configProps> = {
  // This key is used to skip the incentive step and jump ahead to the subscription page.
  // In order to avoid many type exclusions, i defined the type of this key as an empty object.
  highResVectorDownload: {
    title: 'Get High Resolution Downloads',
    description:
      'Upgrade now for pixel-perfect designs on cards, t-shirts and more.',
    items: HighResVectorDownloadContent(),
  },
  premiumDesignUsage: {
    title: 'Get Premium Design Templates',
    description: 'Upgrade your account for unlimited premium access.',
    getItems: PremiumDesignUsage,
    items: [],
  },
  premiumBackgroundUsage: {
    title: 'Get Premium Backgrounds',
    description: 'Upgrade your account for unlimited premium backgrounds.',
    items: PremiumBackgroundUsage(),
  },
  premiumTextureUsage: {
    title: 'Get Premium Textures',
    description: 'Upgrade your account for unlimited premium textures.',
    items: PremiumTextureUsage(),
  },
  premiumElementUsage: {
    title: 'Get Premium Illustrations',
    description: 'Upgrade your account for unlimited premium access.',
    items: PremiumElementUsage(),
  },
  premiumCopilotElementsUsage: {
    title: 'Access 10+ million Premium graphics',
    description:
      'Upgrade now for pixel-perfect designs on cards, T-shirts and more.',
    items: PremiumCopilotElementsUsage(),
    hideButtonDescription: true,
  },
  premiumCopilotTemplatesUsage: {
    title: 'Access 10,000+ Premium templates',
    description:
      'Upgrade now for pixel-perfect designs on cards, T-shirts and more.',
    items: PremiumCopilotElementsUsage(),
    hideButtonDescription: true,
  },
  premiumLayoutUsage: {
    title: 'Upgrade Your Account',
    description:
      'Upgrade now for unlimited text layouts, font pairings, and badge designs.',
    items: PremiumLayoutUsage(),
  },
  premiumMockupUsage: {
    title: 'Use Premium Mockups',
    description: 'Upgrade your account for unlimited premium access.',
    items: PremiumMockupUsage(),
  },
  projectLimitReached: {
    title: 'Unlock Your Next Project',
    getDescription: projectLimitReachedDescription,
    getItems: ProjectLimitReachedContent,
    items: [],
  },
  proProjectLimitReached: {
    title: 'Unlock Your Next Project',
    getDescription: projectLimitReachedDescription,
    getItems: ProProjectLimitReachedContent,
    showOnlyOnePlan: true,
    items: [],
  },
  freeUploadLimitReached: {
    title: 'Need more space?',
    description: 'Upgrade now for advanced upload space.',
    items: UploadLimitReachedContent('10GB'),
  },
  proUploadLimitReached: {
    title: 'Need more space?',
    description: 'Upgrade to Kittl Expert for advanced upload space.',
    items: UploadLimitReachedContent('100GB'),
  },
  removeBackground: {
    title: 'Get unlimited transparent images',
    description:
      'Upgrade now and use unlimited transparent vector downloads for any editable designs.',
    items: RemoveBackgroundContent(),
  },
  uploadCustomFonts: {
    title: 'Need Your Own Fonts?',
    description:
      'Custom font upload is there. Upgrade now to use your own custom fonts for any designs.',
    items: UploadCustomFontsContent(),
  },
  customArtboardSize: {
    title: 'Need Custom Artboards?',
    description:
      'Use custom artboard sizes to create designs in any dimensions for any needs.',
    items: CustomArtboardSizeContent(),
  },
  improveImageQuality: {
    title: 'Need Better Image Quality?',
    description: 'Upgrade now for advanced image enhancements of any designs.',
    items: ImproveImageQualityContent(),
  },
  removeKittlLogo: {
    title: 'Remove Kittl Logo',
    description:
      'Upgrade now to get full commercial licence and use your designs hassle free.',
    items: removeKittlLogoContent(),
  },
  logoGenUpgrade: {
    title: 'Get 4 logos generated each time!',
    description: 'Upgrade now to access more from Logo Generator and Kittl AI.',
    getItems: logoGenUpgradeContent,
    items: [],
  },
  premiumColorPalettes: {
    title: 'Unlock All Color Palettes',
    description: `
      Upgrade now to unlock the full library of special color palettes,
      ready to use with just one click.
    `,
    items: PremiumColorPalettesContent(),
  },
  imageBackgroundRemoval: {
    title: 'Easily Remove Backgrounds',
    description:
      'Upgrade now to remove backgrounds of photos and images with one click.',
    items: ImageBackgroundRemovalContent(),
  },
  unlimitedBackgroundRemoval: {
    title: 'Get Unlimited Background Removal',
    description:
      'Upgrade now for pixel-perfect designs on cards, T-shirts and more.',
    itemsTitle: 'Included:',
    getItems: UnlimitedBackgroundRemovalContent,
    items: [],
  },
  unlimitedImageUpscaling: {
    title: 'Get Unlimited Image Upscaling',
    description:
      'Upgrade now for pixel-perfect designs on cards, T-shirts and more.',
    itemsTitle: 'Included:',
    getItems: UnlimitedBackgroundRemovalContent,
    items: [],
  },
  upgradePlanAsFreeUserAiPanel: {
    ...outOfCredits,
    items: UpgradePlanWithMoreCreditsAsFreeUserAiPanelContent(),
    hideButtonDescription: true,
  },
  upgradePlanWithMoreCreditsAsFreeUserAiPanel: {
    ...outOfCredits,
    items: UpgradePlanWithMoreCreditsAsFreeUserAiPanelContent(),
    hideButtonDescription: true,
  },
  upgradePlanWithMoreCreditsAsProUserAiPanel: {
    ...outOfCredits,
    items: UpgradePlanWithMoreCreditsAsProUserAiPanelContent(),
    hideButtonDescription: true,
    showOnlyOnePlan: true,
  },
  buyMoreCreditsAsExpertUserAiPanel: {
    ...outOfCreditsExpert,
    items: [],
    hideButtonDescription: true,
    showOnlyOnePlan: true,
    buttonLabel: 'Buy Extra Credits Now',
    headerText: 'Buy Extra AI Credits',
  },
  aiRateLimit: {
    title: 'Generate stunning designs with Kittl AI',
    description:
      'You have reached your credit limit. Upgrade now for more credits to use Kittl AI',
    items: AiRateLimitContent(),
  },
  upgradePlanWithMoreCreditsAIPanel: {
    ...outOfCredits,
    items: UpgradePlanWithLimitCredits(),
  },
  imageGenV2Upgrade: {
    title: 'Unlock our highest quality Image Generation',
    ...upgradeNowForMoreCreditsAndAiFeaturesDescription,
    items: ImageGenV2UpgradeContent(),
  },
  vectorizing: {
    title: 'Convert images into vector graphics',
    description:
      'Upgrade now to turn images into vector graphics with one click.',
    items: VectorizingContent(),
  },
  podPresets: {
    title: 'Print on Demand presets',
    description:
      'Upgrade your account to use our growing selection of presets for Print on Demand artwork.',
    items: PodPresetsContent(),
  },
  podDiscounts: {
    title: 'Get cheaper Kittl Print product prices',
    description:
      'Upgrade to Kittl Pro for a 20% discount, or go Expert for 30% off all Kittl Print orders.',
    items: PodDiscountsContent(),
  },
  backgroundReplacer: {
    title: 'Showcase your Products with AI Backgrounds',
    items: BackgroundReplacerContent(),
  },
  upgradeToPaidPlanKittlPro: {
    title: 'Unlock all templates with Kittl Pro',
    description:
      'Upgrade now for pixel-perfect designs on cards, T-shirts and more.',
    items: HighResVectorAccessContent(),
    buttonLabel: `Upgrade`,
    secondaryButtonLabel: 'Maybe Later',
  },
  standAlonePagesTools: {
    title: 'Create Stunning Designs with Kittl-AI',
    description:
      'You have reached your free limit. Upgrade now for premium access.',
    items: StandAlonePagesToolsContent(),
  },
  upgradeToPaidPlan: {
    title: 'Upgrade Your Team',
    description:
      'You have reached the limit of members in your Free team. Upgrade your plan to invite additional members to your team.<br /><br />Upgrade now to invite up to 50 members to your team.',
    items: [],
  },
  freeTrial: {
    ...getFreeTrialConfig({ color: themeStyle.varInkMain }),
    showOnlyOnePlan: true,
  },
  aiImageUpscaler: {
    title: 'Easily upscale images for super resolution',
    description: 'Upgrade now to scale up images with one click.',
    items: AiImageUpscalerContent(),
  },
  aiImageUpscalerCreditsPro: {
    ...outOfCredits,
    items: AiImageUpscalerCreditsProContent(),
  },
  aiSaveAsStyle: {
    title: 'Upload your own images to use as style references!',
    description:
      'Upgrade now to access Image Generator v2 and advanced AI features',
    items: SaveStyleUpgradeContent(),
  },
  aiSaveStyleUpgradeToExpert: {
    title: 'Save more custom styles!',
    description: 'Upgrade now to store up to 30 saved styles at a time.',
    getItems: SaveStyleUpgradeToExpertContent,
    items: [],
    buttonLabel: 'Upgrade Now',
    onlyExpert: true,
  },
  librariesUpgradeToPaidPlan: {
    title: 'Get Brand Kits',
    description:
      'Upgrade now to get access to color, text, border presets and more.',
    items: LibrariesFreeTrialContent(),
    onlyExpert: true,
    showOnlyOnePlan: true,
  },
  unlimitedLibrariesContent: {
    title: 'Get Unlimited Brand Kits',
    description: 'Upgrade to Kittl Expert to add more Brand Kits.',
    items: UnlimitedLibrariesContent(),
  },
  designGenUpgrade: {
    title: 'Get 4 designs generated each time!',
    description: 'Upgrade now to access more from Logo Generator and Kittl AI.',
    getItems: designGenUpgradeContent,
    items: [],
  },
  privateModeUpgrade: {
    title: 'Generate stunning images with Kittl AI in Private Mode',
    description:
      'Upgrade now for more credits and access to advanced AI features',
    items: privateModeUpgradeContent(),
  },
  eraseFill: {
    title: 'Erase objects and let AI automatically fill the background!',
    description: 'Upgrade now to access all the Kittl AI features and more.',
    items: eraseFillContent(),
  },
  /**
   * This type is used to skip the incentive step and go directly to the subscription page.
   */
  noIncentiveUpgrade: {
    title: 'Upgrade your plan',
    items: [],
  },
};

export function getConfigByType(type: UpgradeModalContentType): configProps {
  return config[type];
}

export const getConfig = (
  contentType: UpgradeModalContentType,
  user: User | null,
  sharedConfig: SharedConfigStoreState['config'],
  workspace: Workspace | null
): UpgradeModalConfigData => {
  const data = config[contentType];

  if (data.getDescription) {
    data.description = data.getDescription(workspace);
  }

  if (data.getItems) {
    data.items = data.getItems(user, sharedConfig);
  }

  // TODO remove on cleanup task https://app.clickup.com/t/86bwv18c5
  if (contentType === 'upgradePlanWithMoreCreditsAIPanel') {
    data.items = UpgradePlanWithLimitCredits();
  }

  return data;
};

const Quote: React.FC = () => {
  return (
    <TestimonialWrapper>
      <Testimonial
        compact
        image="/images/upgrade/ChrisSpooner.png"
        name="CHRIS SPOONER"
        text={`It's definitely a one-of-a-kind tool that is of a higher grade than the kinds of tools and resources Adobe are releasing.`}
        title="FOUNDER OF SPOON GRAPHICS"
      />
    </TestimonialWrapper>
  );
};

const QuoteV2: React.FC = () => {
  return (
    <TestimonialWrapper>
      <Testimonial
        compact
        image="/images/upgrade/WillPaterson.png"
        name="WILL PATERSON"
        text="Kittl has been an invaluable resource for our company this year."
        title="LETTERING ARTIST"
      />
    </TestimonialWrapper>
  );
};

const Testimonials: React.FC = () => {
  return (
    <TestimonialWrapper>
      <FlexColumn gap="17px" width="100%">
        <MinimalTestimonial
          image="/images/upgrade/ChrisSpooner.png"
          name="CHRIS SPOONER"
          size={MinimalTestimonialSize.Medium}
          title="FOUNDER OF SPOON GRAPHICS"
        />
        <MinimalTestimonial
          image="/images/upgrade/WillPaterson.png"
          name="WILL PATERSON"
          size={MinimalTestimonialSize.Large}
          title="LETTERING ARTIST"
        />
        <MinimalTestimonial
          image="/images/upgrade/ViktorBaltus.png"
          name="VIKTOR BALTUS"
          size={MinimalTestimonialSize.Small}
          title="TYPE & GRAPHIC DESIGNER"
        />
      </FlexColumn>
    </TestimonialWrapper>
  );
};

const Brands: React.FC = () => {
  return (
    <BrandsWrapper>
      <P2
        color={themeStyle.inkBase}
        style={{ textAlign: 'center', marginTop: 'auto' }}
      >
        Used by brands like:
      </P2>
      <Spacer height="8px" />
      <BrandsImage src="/images/upgrade/brands.png" />
    </BrandsWrapper>
  );
};

const DesignPreviewWrapper: React.FC<{ designPreview: string }> = (props) => {
  const url = props.designPreview.startsWith('/images')
    ? props.designPreview
    : buildImageProxyUrl(props.designPreview, {
        width: 390,
        scale: 2,
      });
  return <DesignPreview src={url} />;
};

const TrustpilotWrapper: React.FC = () => {
  return (
    <TestimonialWrapper>
      <div style={{ maxWidth: '328px', maxHeight: '110px', height: '100%' }}>
        <TrustpilotReview
          author="David N."
          text={
            <span>
              “My sales have <b>increased 550%</b> in just 3 months with AI
              backgrounds”
            </span>
          }
          title="Shopify Shop Owner"
        />
      </div>
    </TestimonialWrapper>
  );
};

export const imageComponents = {
  quote: Quote,
  quoteV2: QuoteV2,
  testimonials: Testimonials,
  brands: Brands,
  designPreview: DesignPreviewWrapper,
  aiImages: Brands,
  backgroundReplacer: TrustpilotWrapper,
  upgradeToPaidPlan: (): null => null,
  designGenUpgrade: (): null => null,
  aiSaveStyleUpgradeToExpert: (): null => null,
  logoGenUpgrade: (): null => null,
  premiumElements: DesignPreviewWrapper,
  copilotTemplates: DesignPreviewWrapper,
  eraseFill: (): null => null,
  none: (): null => null,
};

/**
 * get the background based on the chosen component type
 * @param {string} imageComponentType
 */
export const getBackground = (
  imageComponentType: ImageComponentType
): string => {
  switch (imageComponentType) {
    case 'brands':
      return 'url(/images/upgrade/UpgradeBrandsBackground.jpg)';
    case 'designPreview':
      return themeStyle.transparent;
    case 'aiImages':
      return 'url(/images/upgrade/UpgradeAiBackground.jpg)';
    case 'copilotTemplates':
      return themeStyle.backgroundGradientFull;
    case 'premiumElements':
      return themeStyle.backgroundGradientFull;
    case 'backgroundReplacer':
      return 'url(/images/upgrade/BackgroundReplacerBackground.jpg)';
    case 'upgradeToPaidPlan':
      return 'url(/images/upgrade/UpgradeToPaidPlanBackground.png)';
    case 'logoGenUpgrade':
      return 'url(/images/upgrade/UpgradeGenImage.jpg)';
    case 'designGenUpgrade':
      return 'url(/images/upgrade/UpgradeGenImage.jpg)';
    case 'aiSaveStyleUpgradeToExpert':
      return 'url(/images/upgrade/SaveStyleUpgradeToExpert.jpg)';
    case 'eraseFill':
      return 'url(/images/upgrade/UpgradeAiEraser.png)';
    case 'none':
    default:
      return 'url(/images/upgrade/UpgradeBackground.png)';
  }
};
